import './RouteSwitch.scss';

import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { Layout, Skeleton } from 'antd';
import {
  ClinicianRoutePrefix,
  IntegrationRoutes,
  PatientRoutePrefix,
  routes,
  Routes
} from 'routes';

import { ConnectRouterState, useAppSelector } from 'lib/store/rootReducer';

import { ConnectedRxProgressBar, Footer } from 'components';

import {
  applyFilter,
  routesWithWhiteBg,
  siteProgressBarRoutes
} from 'lib/constants/filters';

import { PrivacyPolicy, TermsOfService } from 'features/terms-of-service';

import { AcceptInvite } from 'features/accept-invite/accept-invite';
import { GenerateClinicSignupLinkPage } from 'features/admin/generate-clinic-signup-link/page';
import { OrganisationManagementPage } from 'features/admin/organisation-management/page';
import { Role } from 'features/auth/types';
import OnboardingLandingPage from 'features/clinic/onboarding/registration/[no-auth]/page-organisation-onboarding-landing';
import RegistrationFlow from 'features/clinic/onboarding/registration/[no-auth]/layout-registration_flow';
import OnboardingSetupPage from 'features/clinic/onboarding/registration/[auth]/OnboardingSetupPage';
import { AssessmentLinkPage } from 'features/patient/assessment-link/page-assessment-link';
import { ForgotPassword } from 'features/sign-in/v2/page-forgot-password';
import { CreateAccount } from 'features/sign-in/v2/page-init-account';
import { LoginForm } from 'features/sign-in/v2/page-sign-in';
import { useAuth } from 'lib/hooks/auth/use-auth';
import { ConditionalNavigationMenu } from 'navigation/menu/menu';
import { PublicRoute } from 'navigation/router/components/routes/public-route';
import { PrivateRoute } from '../components/routes/private-route';
import { RouteSwitchIntegration } from './route-switch-integration';
import { RouteSwitchUser } from './router-user';

export function RouteSwitch() {
  const backgroundColor = useAppSelector(state => state.uiPref.backgroundColor);
  const authState = useAuth();
  const loc = useLocation<ConnectRouterState | undefined>();
  const location = loc.pathname;

  function renderHeaderProgressBar() {
    if (
      siteProgressBarRoutes.findIndex(fn =>
        typeof fn === 'function' ? fn(location) : location === fn
      ) !== -1
    ) {
      return (
        <ConnectedRxProgressBar
          className="no-top-margin flat-ends"
          strokeLinecap="round"
        />
      );
    }
    return null;
  }

  function getBackgroundColor() {
    if (
      routesWithWhiteBg.some(match =>
        typeof match === 'function' ? match(location) : match === location
      )
    ) {
      return 'white';
    }

    return backgroundColor;
  }

  // if (authState.hasError) {
  //   const err = authState.error ?? new Error('Authentication error');
  //   return (
  //     <ErrorDisplay
  //       error={err}
  //       title="Authentication Error"
  //       description="An error occurred during authentication."
  //     />
  //   );
  // }
  if (authState.isUserDataLoading) {
    return <Skeleton active />;
  }

  return (
    // TODO dynamic background color assignment through reducer
    <Layout
      className="site-layout"
      style={{ backgroundColor: getBackgroundColor() }}
    >
      <ConditionalNavigationMenu />
      {renderHeaderProgressBar()}
      <Layout style={{ backgroundColor: getBackgroundColor() }}>
        <Layout.Content>
          <Switch>
            <PublicRoute
              path={'/accept/invite'}
              component={AcceptInvite}
              redirectPath={'/'}
            />
            <PublicRoute
              exact
              path={routes.onboarding.landing}
              component={OnboardingLandingPage}
              redirectPath={'/'}
            />
            <PublicRoute
              path={routes.onboarding.registration}
              component={RegistrationFlow}
              redirectPath={'/'}
            />
            <PrivateRoute
              roles={[Role.ClinicAdmin, Role.Clinician]}
              path={routes.onboarding.setup}
              component={OnboardingSetupPage}
              redirectPath={'/'}
            />
            <PublicRoute
              path={Routes.SignInSuffix}
              component={LoginForm}
              publicOnly
              redirectPath={'/'}
            />
            <PrivateRoute
              roles={[Role.GlobalAdmin, Role.ClinicAdmin, Role.Clinician, Role.Patient]}
              path={Routes.CreateAccountSuffix}
              component={CreateAccount}
              redirectPath={'/'}
            />
            <PublicRoute
              path={Routes.ForgotPassword}
              component={ForgotPassword}
              redirectPath={'/'}
            />
            <PublicRoute
              path={Routes.TermsOfService}
              component={TermsOfService}
              redirectPath={'/'}
            />
            <PublicRoute
              path={Routes.PrivacyPolicy}
              component={PrivacyPolicy}
              redirectPath={'/'}
            />
            <PrivateRoute
              path={['/clinician', '/patient']}
              roles={'*'}
              component={RouteSwitchUser}
            />
            <PrivateRoute
              roles={[]}
              temp__extraRoles={[Role.GlobalAdmin]}
              path={routes.admin.organisationManagement}
              component={OrganisationManagementPage}
              redirectPath={'/'}
            />
            <PrivateRoute
              roles={[]}
              temp__extraRoles={[Role.GlobalAdmin]}
              path={routes.admin.clinicSignupLinkGeneration}
              component={GenerateClinicSignupLinkPage}
              redirectPath={'/'}
            />

            <PublicRoute
              path={Routes.AssessmentLinkEntrypoint}
              redirectPath={'/'}
              component={AssessmentLinkPage}
            />
            <Route
              path={IntegrationRoutes.Prefix}
              component={RouteSwitchIntegration}
            />

            <Route
              // exact
              path={'/'}
              render={() => {
                // If there is no user
                if (!authState.user) {
                  // Redirect to the patient sign in
                  return <Redirect to={Routes.SignInSuffix} />;
                }

                if (authState.user.needsPassword || authState.user.isPartial) {
                  return <Redirect to={Routes.CreateAccountSuffix} />;
                }

                // Otherwise the user is signed in. Redirect the user to their home page
                return (
                  <Redirect
                    to={
                      [Role.Clinician, Role.ClinicAdmin].includes(
                        authState.user.role
                      )
                        ? ClinicianRoutePrefix
                        : PatientRoutePrefix
                    }
                  />
                );
              }}
            />
          </Switch>
        </Layout.Content>
        <Layout.Footer>
          {!applyFilter(location, routesWithWhiteBg) && <Footer />}
        </Layout.Footer>
      </Layout>
    </Layout>
  );
}
